/**
 * Connectors: shared DTOs for the two-rail Connectors feature (M3).
 *
 * Mirrors the Prisma models in `packages/db/prisma/schema/connectors.prisma`.
 * RAIL "tool" connects through Nango (Tabbio stores only a Nango connection id,
 * never a token); RAIL "ai" exposes Tabbio as an MCP server (the Connection
 * references an mcpClientId). None of these DTOs carry provider credentials.
 *
 * Runtime const arrays (CONNECTOR_RAILS, CONNECTOR_STATUSES, …) are re-exported
 * through `connector.runtime.js` so nodenext ESM consumers resolve them: import
 * the values from `@tabbio/types` (which points at the `.runtime.js`) and the
 * types from here.
 */

// ── Enumerations (const arrays back the unions; see connector.runtime.js) ──────

export const CONNECTOR_RAILS = ['tool', 'ai'] as const;
export type ConnectorRail = (typeof CONNECTOR_RAILS)[number];

export const CONNECTOR_STATUSES = [
  'available',
  'coming_soon',
  'disabled',
] as const;
export type ConnectorStatus = (typeof CONNECTOR_STATUSES)[number];

export const CONNECTION_STATUSES = [
  'pending',
  'active',
  'revoked',
  'error',
] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number];

export const CONNECTION_CREDENTIAL_STATUSES = [
  'unconfigured',
  'valid',
  'expired',
  'error',
] as const;
export type ConnectionCredentialStatus =
  (typeof CONNECTION_CREDENTIAL_STATUSES)[number];

export const TRIGGER_EVENT_STATUSES = [
  'pending',
  'processed',
  'failed',
] as const;
export type TriggerEventStatus = (typeof TRIGGER_EVENT_STATUSES)[number];

export const ACTION_JOB_STATUSES = [
  'pending',
  'running',
  'succeeded',
  'failed',
] as const;
export type ActionJobStatus = (typeof ACTION_JOB_STATUSES)[number];

export const CONNECTOR_AUDIT_EVENT_TYPES = [
  'connect',
  'revoke',
  'sync',
  'action',
  'trigger',
  'mcp_tool_call',
] as const;
export type ConnectorAuditEventType =
  (typeof CONNECTOR_AUDIT_EVENT_TYPES)[number];

/** Phase-1 catalog slugs (kept in lockstep with connector-catalog.ts). */
export const CONNECTOR_SLUGS = [
  'gmail',
  'slack',
  'zapier',
  'make',
  'linkedin',
  'chatgpt',
  'claude',
  'gemini',
  'grok',
  'perplexity',
] as const;
export type ConnectorSlug = (typeof CONNECTOR_SLUGS)[number];

// ── Catalog + connection DTOs ─────────────────────────────────────────────────

/** A single granted/requested scope rendered for humans. */
export interface ConnectorScopeDisplay {
  scope: string;
  /** Human-readable phrase, e.g. "Read emails in a label". */
  label: string;
  /** True for scopes that can take a side-effecting / write action. */
  write?: boolean;
}

/** Catalog entry (one connectable service). */
export interface Connector {
  slug: ConnectorSlug | string;
  name: string;
  rail: ConnectorRail;
  description: string;
  iconKey?: string | null;
  /** Nango integration id (tool rail only). */
  nangoIntegrationId?: string | null;
  defaultScopes: string[];
  /** Human-readable scope phrases for the default scopes. */
  scopeDisplays?: ConnectorScopeDisplay[];
  supportedTriggerTypes: string[];
  supportedActionTypes: string[];
  minimumTier: string;
  status: ConnectorStatus;
  /** Phase-1 webhook-target connectors (Zapier/Make) vs OAuth (Gmail/Slack). */
  authKind?: 'oauth' | 'webhook_target' | 'mcp';
}

/** A user's connection row (NEVER carries tokens). */
export interface Connection {
  id: string;
  connectorSlug: ConnectorSlug | string;
  rail: ConnectorRail;
  userId: string;
  companyId?: string | null;
  /** Tool rail: the Nango connection id: NOT a token. */
  nangoConnectionId?: string | null;
  /** AI rail: the registered MCP/oauth client id. */
  mcpClientId?: string | null;
  config: Record<string, unknown>;
  status: ConnectionStatus | string;
  credentialStatus: ConnectionCredentialStatus | string;
  scopes: string[];
  lastSyncedAt?: string | Date | null;
  lastError?: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
}

/** Catalog entry + the viewer's connection (if any), for list rendering. */
export interface ConnectorListEntry {
  connector: Connector;
  connection: Connection | null;
}

/** Per-rail caps from the active plan (null = unlimited). */
export interface ConnectorListCaps {
  connectedToolsMax: number | null;
  connectedToolsUsed: number;
  connectedAiMax: number | null;
  connectedAiUsed: number;
}

/** GET /api/connectors: catalog + connections grouped by rail. */
export interface ConnectorListResponse {
  rails: {
    tool: ConnectorListEntry[];
    ai: ConnectorListEntry[];
  };
  caps: ConnectorListCaps;
}

// ── Trigger / action DTOs ─────────────────────────────────────────────────────

export interface TriggerEventDTO {
  id: string;
  connectionId: string;
  connectorSlug: ConnectorSlug | string;
  triggerDefinitionId?: string | null;
  externalObjectId?: string | null;
  status: TriggerEventStatus | string;
  attempts: number;
  payload: Record<string, unknown>;
  createdAt: string | Date;
}

export interface ActionJobDTO {
  id: string;
  connectionId?: string | null;
  connectorSlug: ConnectorSlug | string;
  actionDefinitionId?: string | null;
  status: ActionJobStatus | string;
  attempts: number;
  result?: Record<string, unknown> | null;
  error?: string | null;
  createdAt: string | Date;
  completedAt?: string | Date | null;
}

// ── Request payloads (the `data` under {data,error,meta}) ──────────────────────

export interface ConnectorSessionRequest {
  connectorSlug: ConnectorSlug | string;
  /** Optional return/callback url the client wants after Connect completes. */
  returnUrl?: string;
}

export interface ConnectorSessionResult {
  /** Short-lived Nango Connect session token. */
  sessionToken: string;
  expiresAt?: string | null;
  connectorSlug: ConnectorSlug | string;
}

export interface ConnectorConnectRequest {
  /** Tool rail: the Nango connection id returned by the Connect UI. */
  nangoConnectionId?: string;
  /** Minimal, non-secret config (Gmail label / Slack channel ids). */
  config?: Record<string, unknown>;
}

export interface ConnectorConnectResult {
  connection: Connection;
}

export interface ConnectorScopesResult {
  connectorSlug: ConnectorSlug | string;
  scopes: ConnectorScopeDisplay[];
}
